'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/I18nProvider';

/**
 * Project data structure used by the platform project viewer.
 * Differs from VinHub's HubProject — here we receive data from
 * the platform's sector/project API.
 */
export interface PlatformProject {
    id: string | number;
    url: string;
    title?: string;
    display_vn?: string;
    display_en?: string;
    display_ja?: string;
    thumbnail?: string;
    field?: string;
}

interface PlatformProjectViewerModalProps {
    project: PlatformProject | null;
    onClose: () => void;
    iframeRef: React.RefObject<HTMLIFrameElement | null>;
}

/**
 * Fullscreen modal that embeds a sub-project via iframe.
 * Mirrors VinGroupHub's ProjectViewerModal, adapted for
 * the Starglobal3D platform context.
 */
export default function PlatformProjectViewerModal({
    project,
    onClose,
    iframeRef,
}: PlatformProjectViewerModalProps) {
    const { locale } = useLanguage();
    const text = (vn: string, en: string) => locale === 'en' ? en : vn;

    const projectUrl = project?.url ?? null;

    // Mute audio and skip intro when embedding via iframe to avoid audio overlap with agent
    const iframeUrl = projectUrl ? (() => {
        try {
            const url = new URL(projectUrl, window.location.origin);
            url.searchParams.set('mute', '1');
            url.searchParams.set('skipIntro', '1');
            return projectUrl.startsWith('/') ? url.pathname + url.search + url.hash : url.toString();
        } catch {
            return projectUrl;
        }
    })() : null;

    const projectId = project?.id;
    const [isLoading, setIsLoading] = useState(true);
    const backButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        setIsLoading(true);
    }, [projectUrl]);

    // Focus trap and ESC key handler
    useEffect(() => {
        if (!projectId) return;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        backButtonRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            requestAnimationFrame(() => { if (previouslyFocused?.isConnected) previouslyFocused.focus(); });
        };
    }, [projectId, onClose]);

    // Lock body scroll while modal is open
    useEffect(() => {
        if (!project) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previousOverflow; };
    }, [project]);

    if (!project) return null;

    const displayName = locale === 'en'
        ? (project.display_en || project.title || project.display_vn || '')
        : locale === 'ja'
            ? (project.display_ja || project.display_en || project.title || project.display_vn || '')
            : (project.display_vn || project.title || project.display_en || '');

    const handleReload = () => {
        if (iframeRef.current) {
            setIsLoading(true);
            iframeRef.current.src = iframeUrl!;
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={text(`Dự án ${displayName}`, `${displayName} project`)}
            className="fixed inset-0 z-[150] flex flex-col bg-[#060a13] animate-in fade-in duration-200"
        >
            {/* Top Navigation Bar */}
            <div className="h-14 gap-1 px-2 sm:px-4 bg-[rgba(6,10,19,0.96)] border-b border-white/10 flex items-center justify-between shrink-0 select-none backdrop-blur-xl">
                {/* Back Button */}
                <div className="flex items-center gap-3">
                    <button
                        ref={backButtonRef}
                        aria-label={text('Quay lại Platform', 'Back to Platform')}
                        onClick={onClose}
                        className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-slate-200 hover:text-white text-xs font-semibold transition-all"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        <span className="hidden sm:inline">{text('Quay lại', 'Back')}</span>
                    </button>
                </div>

                {/* Center: Project Title */}
                <div className="hidden min-w-0 items-center gap-2 text-xs lg:flex">
                    <span className="font-bold text-white tracking-wide truncate max-w-[400px]">{displayName}</span>
                    {project.field && (
                        <span className="hidden md:inline-block text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-white/10 text-cyan-300">
                            {project.field}
                        </span>
                    )}
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-2">
                    {/* Reload */}
                    <button
                        onClick={handleReload}
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                        title={text('Tải lại không gian 3D', 'Reload 3D space')}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>

                    {/* Open in New Window */}
                    <a
                        href={projectUrl!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                        title={text('Mở trong tab mới', 'Open in a new tab')}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                    </a>

                    {/* Close */}
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors ml-1"
                        title={text('Đóng dự án', 'Close project')}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Iframe Viewport Container */}
            <div className="relative flex-1 w-full h-full bg-[#050811] overflow-hidden">
                {isLoading && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#060a13] text-slate-300">
                        <div className="w-10 h-10 rounded-full border-2 border-cyan-500/30 border-t-cyan-400 animate-spin mb-3" />
                        <p className="text-xs font-semibold tracking-wider text-slate-300">
                            {text(`Đang tải dự án...`, `Loading project...`)}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-1">{displayName}</p>
                    </div>
                )}
                <iframe
                    title={text(`Dự án ${displayName}`, `${displayName} project`)}
                    ref={iframeRef}
                    src={iframeUrl!}
                    className="w-full h-full border-0 bg-[#060a13]"
                    allow="fullscreen; web-share; xr-spatial-tracking; microphone; camera"
                    allowFullScreen
                    onLoad={() => setIsLoading(false)}
                />
            </div>
        </div>
    );
}
